import { Factory } from "@/data/dao/Factory";
import { UserDao } from "@/data/dao/service/UserDao";
import { OperatorCancellationClaimValue, OperatorItemAvailabilityAndStockLevelClaimValue, OperatorReportClaimValue, OperatorTransferClaimValue } from "@/data/model/Enums";
import type { IUserAccessGrant } from "@/data/model/User";
import { claimTypes } from "@/data/util/Config";
import { Misc } from "@/data/util/Misc";



import { getLocationName } from "@/shared/helpers/LocationHelper";
import { logError } from "@/shared/logging/errors";



import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { Select } from "@/portal/formControls/Select";
import { UserAccessWindow } from "@/portal/modal/UserAccessWindow";
import { UiNotification } from "@/portal/ui/Notification";


export class UserAccessEditor {
    private $element: JQuery;
    private dao?: UserDao;
    private userId?: string;

    private search?: TableFilter;
    private dataTable?: DataTable<IUserAccessGrant>;
    private takenClaimValues = new Array<string>();
    private reportPermissionsSelect: Select<OperatorReportClaimValue>;
    private cancellationPermissionsSelect: Select<OperatorCancellationClaimValue>;
    private transferPermissionsSelect: Select<OperatorTransferClaimValue>;
    private itemAvailabilityAndStockLevelSelect: Select<OperatorItemAvailabilityAndStockLevelClaimValue>;
    private $savePermissionsBtn: JQuery;

    constructor(element: JQuery) {
        this.$element = element;

        const reportPermissionsSelect = this.$element.find('.form-control[name="report-permissions-select"]');
        Misc.eachEnum(OperatorReportClaimValue, (i, e) => {
            reportPermissionsSelect.append(
                '<option value="' + i + '">' + i18n("enums.ReportClaimValue." + e) + "</option>"
            );
        });
        this.reportPermissionsSelect = Select.createFromSelect(reportPermissionsSelect);

        const cancellationPermissionsSelect = this.$element.find(
            '.form-control[name="cancellation-permissions-select"]'
        );
        Misc.eachEnum(OperatorCancellationClaimValue, (i, e) => {
            cancellationPermissionsSelect.append(
                '<option value="' + i + '">' + i18n("enums.CancellationClaimValue." + e) + "</option>"
            );
        });

        this.cancellationPermissionsSelect = Select.createFromSelect(cancellationPermissionsSelect);

        const transferPermissionsSelect = this.$element.find('.form-control[name="transfer-permissions-select"]');
        Misc.eachEnum(OperatorTransferClaimValue, (i, e) => {
            transferPermissionsSelect.append(
                '<option value="' + i + '">' + i18n("enums.OperatorTransferClaimValue." + e) + "</option>"
            );
        });

        this.transferPermissionsSelect = Select.createFromSelect(transferPermissionsSelect);

        const itemAvailabilityAndStockLevelSelect = this.$element.find(
            '.form-control[name="item-availability-and-stock-level-permissions-select"]'
        );
        Misc.eachEnum(OperatorItemAvailabilityAndStockLevelClaimValue, (i, e) => {
            itemAvailabilityAndStockLevelSelect.append(
                '<option value="' +
                    i +
                    '">' +
                    i18n("enums.OperatorItemAvailabilityAndStockLevelClaimValue." + e) +
                    "</option>"
            );
        });

        this.itemAvailabilityAndStockLevelSelect = Select.createFromSelect(itemAvailabilityAndStockLevelSelect);

        this.$savePermissionsBtn = this.$element.find("#save-permissions-btn");
        this.$savePermissionsBtn.click(async (e) => {
            e.preventDefault();
            e.stopPropagation();

            await this.updateGrants();
        });
    }

    public loadForUser(userId: string): UserAccessEditor {
        if (this.dao === undefined || this.dataTable === undefined) {
            this.dao = Factory.getInstance<UserDao>(UserDao);

            this.userId = userId;

            const tableConfig: IDataTableConfig<IUserAccessGrant> = {
                columnMapping: [
                    {
                        name: i18n("user.fields.claimType"),
                        property: "claimType",
                        sortable: false,
                        styles: { width: "40%" },
                    },
                    {
                        name: i18n("general.entities.location"),
                        property: "location",
                        sortable: false,
                        styles: { width: "45%" },
                        formatter: (m, location) => (location == null ? "" : getLocationName(location)),
                    },
                    {
                        name: "",
                        property: "id",
                        styles: { width: "110px", "text-align": "right" },
                        sortable: false,
                        formatter: (m) => {
                            return $(
                                '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-trash"></i></a></span>'
                            ).click((e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                UiNotification.confirm(
                                    i18n("user.reallyRemoveGrant"),
                                    undefined,
                                    () => {
                                        $(e.currentTarget).replaceWith(
                                            '<div class="kt-spinner kt-spinner--sm kt-spinner--success pull-right mr-4"></div>'
                                        );
                                        this.onRevokeGrant(m);
                                    },
                                    i18n("user.removeGrant")
                                );
                            });
                        },
                    },
                ],
                autoload: false,
                paginated: false,
                useJsHistory: false,
                noDatatext: i18n("user.noGrants"),
                dataEvent: (m) => {
                    this.takenClaimValues = new Array<string>();
                    for (const i in m) {
                        const acE = m[i];
                        if (acE.location !== undefined) this.takenClaimValues.push(acE.location.id);
                    }
                },
                filterProvider: {
                    getFilter: () => this.search!.getValue(),
                    setFilter: (d) => this.search!.setValue(d),
                },
            };

            this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body > .tablefilter"));
            this.dataTable = new DataTable<IUserAccessGrant>(
                this.$element.find("> .kt-portlet__body > .kt-datatable"),
                this.dao.aclDataSource(this.userId),
                tableConfig
            );
            this.search.addAction(
                i18n("user.newGrant"),
                () => {
                    const window = new UserAccessWindow(this.userId!, this.takenClaimValues);
                    window.onSaved = () => this.dataTable!.loadData();
                    window.show();
                },
                "la la-plus"
            );

            this.dataTable.listenTo(this.search);
            this.dataTable.loadData();
        }

        // load grants
        this.loadGrants();

        return this;
    }

    private loadGrants() {
        this.dao!.getGrants(this.userId!, {}).then((grants) => {
            this.updateGrantsInUi(grants.items);
        });
    }

    private updateGrantsInUi(grants: IUserAccessGrant[]) {
        const reportGrant = grants.find((g) => g.claimType === claimTypes.operatorReportGrant);
        if (reportGrant != null) {
            this.reportPermissionsSelect.setValue(reportGrant.claimValue);
        } else {
            this.reportPermissionsSelect.setValue(OperatorReportClaimValue.ViewOnlyOwnSalesAndRevenue);
        }

        const cancellationGrant = grants.find((g) => g.claimType === claimTypes.operatorCancellationGrant);
        if (cancellationGrant != null) {
            this.cancellationPermissionsSelect.setValue(cancellationGrant.claimValue);
        } else {
            this.cancellationPermissionsSelect.setValue(OperatorCancellationClaimValue.None);
        }

        const transferGrant = grants.find((g) => g.claimType === claimTypes.operatorTransferGrant);
        if (transferGrant != null) {
            this.transferPermissionsSelect.setValue(transferGrant.claimValue);
        } else {
            this.transferPermissionsSelect.setValue(OperatorTransferClaimValue.CannotTransfer);
        }

        const operatorItemAvailabilityAndStockLevelGrant = grants.find(
            (g) => g.claimType === claimTypes.operatorItemAvailabilityAndStockLevelGrant
        );
        if (operatorItemAvailabilityAndStockLevelGrant != null) {
            this.itemAvailabilityAndStockLevelSelect.setValue(operatorItemAvailabilityAndStockLevelGrant.claimValue);
        } else {
            this.itemAvailabilityAndStockLevelSelect.setValue(
                OperatorItemAvailabilityAndStockLevelClaimValue.CannotEdit
            );
        }
    }

    private async updateGrants() {
        const grants = await this.dao!.updateUserGrants(this.userId!, {
            operatorReportClaimValue: parseInt(this.reportPermissionsSelect.getValue()!),
            operatorCancellationClaimValue: parseInt(this.cancellationPermissionsSelect.getValue()!),
            operatorTransferClaimValue: parseInt(this.transferPermissionsSelect.getValue()!),
            operatorItemAvailabilityAndStockLevelClaimValue: parseInt(this.itemAvailabilityAndStockLevelSelect.getValue()!),
        });

        UiNotification.showSuccess(i18n("general.successfullyUpdated"));

        this.updateGrantsInUi(grants.items);
    }

    private onRevokeGrant(grant: IUserAccessGrant) {
        Factory.getInstance<UserDao>(UserDao)
            .removeAccess(this.userId!, grant.claimId)
            .then(() => {
                this.dataTable!.loadData();
            })
            .catch((ex) => {
                logError(ex);
                this.dataTable!.loadData();
            });
    }
}
